import { Link, useParams } from "@tanstack/react-router";
import { ArrowLeft, ExternalLink } from "lucide-react";
import { digestDetail } from "app/router";
import { MarkdownContent, ProseDocument, Spinner } from "components/elements";
import { useUserTimezone } from "hooks/useUserTimezone";
import { trpc } from "lib/trpc";

export const AIDigestDetail = () => {
    const { id } = useParams({ from: digestDetail.id });
    const numericId = Number(id);
    const timeZone = useUserTimezone();

    const {
        data: summary,
        isPending: isLoading,
        isError,
    } = trpc.digest.getSummaryById.useQuery({ id: numericId });

    if (isLoading) {
        return (
            <div className="mx-auto w-full pt-6">
                <Spinner className="mx-auto" />
            </div>
        );
    }

    if (isError || !summary) {
        return (
            <div className="mx-auto w-full pt-6">
                <Link
                    to="/digest"
                    className="mb-4 inline-flex items-center gap-1 text-sm text-blue-600 hover:underline"
                >
                    <ArrowLeft className="h-4 w-4" /> Back to Digests
                </Link>
                <div className="py-8 text-center text-red-500">Digest not found.</div>
            </div>
        );
    }

    return (
        <div className="mx-auto w-full pt-6">
            <Link
                to="/digest"
                className="mb-4 inline-flex items-center gap-1 text-sm text-blue-600 hover:underline"
            >
                <ArrowLeft className="h-4 w-4" /> Back to Digests
            </Link>

            <div className="mt-4 rounded-2xl border border-[#c8c8c8] bg-[#fafffe] p-5">
                {summary.featuredImageURL && (
                    <img
                        src={summary.featuredImageURL}
                        alt=""
                        className="mb-4 h-48 w-full rounded-lg object-cover"
                    />
                )}

                <h1 className="mb-2 font-sans text-[24px] font-semibold text-[#424242]">
                    {summary.title}
                </h1>

                <p className="mb-4 text-sm text-gray-500">
                    {new Intl.DateTimeFormat("en-US", {
                        month: "long",
                        day: "2-digit",
                        year: "numeric",
                        hour: "numeric",
                        minute: "2-digit",
                        hour12: true,
                        timeZone,
                    }).format(new Date(summary.createdAt))}
                </p>

                {summary.summary && (
                    <p className="mb-4 rounded-lg bg-blue-50 p-3 text-sm italic text-gray-700">
                        {summary.summary}
                    </p>
                )}

                <div className="break-words text-sm text-[#424242]">
                    {summary.contentJson ? (
                        <ProseDocument doc={summary.contentJson} />
                    ) : (
                        <MarkdownContent markdown={summary.content} />
                    )}
                </div>
            </div>

            <SourceArticles summaryId={numericId} />
            <SimilarDigests summaryId={numericId} />
        </div>
    );
};

const SourceArticles = ({ summaryId }: { summaryId: number }) => {
    const { data, isPending } = trpc.digest.getDigestArticles.useQuery({ summaryId });

    if (isPending) {
        return (
            <div className="mt-6">
                <h2 className="mb-3 font-sans text-[18px] font-semibold text-[#424242]">
                    Source Articles
                </h2>
                <Spinner className="mx-auto" />
            </div>
        );
    }

    if (!data?.articles?.length) {
        return null;
    }

    return (
        <div className="mt-6">
            <h2 className="mb-3 font-sans text-[18px] font-semibold text-[#424242]">
                Source Articles
            </h2>
            <div className="space-y-2">
                {data.articles.map((article) => (
                    <a
                        key={article.id}
                        href={article.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-2 rounded-lg border border-gray-200 bg-white p-3 transition hover:bg-gray-50"
                    >
                        <div className="flex-1">
                            <div className="font-sans text-sm font-medium text-[#424242]">
                                {article.title}
                            </div>
                            {article.feedSourceTitle && (
                                <div className="text-xs text-gray-500">
                                    {article.feedSourceTitle}
                                </div>
                            )}
                        </div>
                        <ExternalLink className="h-4 w-4 flex-shrink-0 text-gray-400" />
                    </a>
                ))}
            </div>
        </div>
    );
};

const SimilarDigests = ({ summaryId }: { summaryId: number }) => {
    const timeZone = useUserTimezone();
    const { data, isPending } = trpc.digest.getSimilarSummaries.useQuery({
        summaryId,
        limit: 5,
    });

    if (isPending) {
        return (
            <div className="mt-6">
                <h2 className="mb-3 font-sans text-[18px] font-semibold text-[#424242]">
                    Similar Digests
                </h2>
                <Spinner className="mx-auto" />
            </div>
        );
    }

    if (!data?.summaries?.length) {
        return null;
    }

    return (
        <div className="mt-6 mb-6">
            <h2 className="mb-3 font-sans text-[18px] font-semibold text-[#424242]">
                Similar Digests
            </h2>
            <div className="space-y-2">
                {data.summaries.map((s) => (
                    <Link
                        key={s.id}
                        to="/digest/$id"
                        params={{ id: String(s.id) }}
                        className="block rounded-lg border border-gray-200 bg-white p-3 transition hover:bg-gray-50"
                    >
                        <div className="font-sans text-sm font-medium text-[#424242]">
                            {s.title}
                        </div>
                        <div className="text-xs text-gray-500">
                            {new Intl.DateTimeFormat("en-US", {
                                month: "long",
                                day: "2-digit",
                                year: "numeric",
                                timeZone,
                            }).format(new Date(s.createdAt))}
                        </div>
                        {s.summary ? (
                            <p className="mt-1 text-xs text-gray-600">{s.summary}</p>
                        ) : (
                            <p className="mt-1 text-xs text-gray-600">
                                {s.content.replace(/<[^>]+>/g, "").slice(0, 100)}...
                            </p>
                        )}
                    </Link>
                ))}
            </div>
        </div>
    );
};
